import React, { useContext, useEffect, useState } from 'react';
import { graphql } from 'gatsby';
import { useI18next } from 'gatsby-plugin-react-i18next';
import styled from '@emotion/styled';
import lozad from 'lozad';

import Layout from '@components/layout';
import SEO from '@components/SEO';
// import useWindowSize from '@hooks/useWindowSize';
import { DispatchContext, StateContext } from '@store/store';

import ScrollToTop from '@components/Buttons/ScrollToTop';
import BlogBody from '@components/Blogs/BlogBody';
import { BreadCrumb, CrumbLink } from '@components/common/BreadCrumb';
import BlogBottom from '@components/Blogs/BlogBottom';
import { openLeadMagnetModal } from '@store/actions';
import loadable from '@loadable/component';
import { ConstantLinks } from '@constants/ConstantLinks';
import useAuth from '@hooks/useAuth';
import StickyCard from '@components/Blogs/StickyCard';
const LeadMagnetModal = loadable(() =>
  import('../../components/LeadMagnetModal'),
);

function BlogTemplate(props) {
  const { data, pageContext } = props;
  const { prev, next, slugPrefix } = props.pageContext;
  const { t, language } = useI18next();
  // const { isMediaMatched } = useWindowSize('max-width: 640px');
  const state = useContext(StateContext);
  const dispatch = useContext(DispatchContext);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const { isUserLoggedIn } = useAuth();
  const isModalVisible = false;

  useEffect(() => {
    const getAllIframes = document.querySelectorAll('iframe[loading="lazy"]');
    const getAllImages = document.querySelectorAll('.lazyload');

    if (getAllIframes) {
      const observer = lozad(getAllIframes); // lazy loads elements with default selector as '.lozad'
      observer.observe();
    }
    if (getAllImages) {
      const observer = lozad(getAllImages); // lazy loads elements with default selector as '.lozad'
      observer.observe();
    }
    setIsLoggedIn(isUserLoggedIn());
  }, []);

  useEffect(() => {
    if (isModalVisible) {
      if (window.location.pathname === '/blog/tiktok-brand-marketing-report/') {
        const downloadLinks = document.querySelector(
          `a[href='${ConstantLinks.leadMagnet}']`,
        );

        downloadLinks.addEventListener('click', e => {
          e.preventDefault();
          dispatch(
            openLeadMagnetModal({
              tab: 'form2',
              link: ConstantLinks.leadMagnet,
            }),
          );
        });

        window.addEventListener('beforeunload', unloadCheck);

        window.addEventListener('scroll', scrollCheck);

        window.addEventListener('blur', onBlur);

        document.addEventListener('mouseleave', hoverOnAddressBar);
      }

      return () => {
        if (window.location.pathname === '/blog/tiktok-marketing/') {
          window.removeEventListener('scroll', scrollCheck);
          window.removeEventListener('blur', onBlur);
          window.removeEventListener('beforeunload', unloadCheck);
          document.removeEventListener('mouseleave', hoverOnAddressBar);
        }
      };
    }
  }, []);

  function scrollCheck() {
    if (
      parseFloat(
        ((window.pageYOffset * 100) / document.body.clientHeight).toFixed(2),
      ) > 25
    ) {
      dispatch(
        openLeadMagnetModal({
          tab: 'form3',
          link:
            'https://drive.google.com/drive/folders/1DzZ5yVeh-EU6j45m-YM6bHd8fIALZc9u?usp=sharing',
        }),
      );
      window.removeEventListener('scroll', scrollCheck);
    }
  }

  function unloadCheck(e) {
    const confirmationMessage = 'o/';
    dispatch(
      openLeadMagnetModal({
        tab: 'form1',
        link:
          'https://drive.google.com/drive/folders/1DzZ5yVeh-EU6j45m-YM6bHd8fIALZc9u?usp=sharing',
      }),
    );
    (e || window.event).returnValue = confirmationMessage; //Gecko + IE
    return confirmationMessage; //Webkit, Safari, Chrome
  }

  function hoverOnAddressBar(e) {
    let isLeaving = e.pageY - window.pageYOffset;
    if (isLeaving <= 50) {
      dispatch(
        openLeadMagnetModal({
          tab: 'form1',
          link:
            'https://drive.google.com/drive/folders/1DzZ5yVeh-EU6j45m-YM6bHd8fIALZc9u?usp=sharing',
        }),
      );
      document.removeEventListener('mouseleave', hoverOnAddressBar);
    }
  }

  function onBlur() {
    const getAllIframes = document.querySelectorAll('iframe');
    let clickIframe = false;
    getAllIframes.forEach(item => {
      if (document.activeElement == item) {
        clickIframe = true;
      }
    });
    if (clickIframe) return;
    dispatch(
      openLeadMagnetModal({
        tab: 'form1',
        link:
          'https://drive.google.com/drive/folders/1DzZ5yVeh-EU6j45m-YM6bHd8fIALZc9u?usp=sharing',
      }),
    );
    window.removeEventListener('blur', onBlur);
  }

  return (
    <Layout isBlog>
      <SEO
        t={t}
        slug={pageContext.pageSlug}
        seo={data.strapiBlog.SEO}
        lang={language}
        FAQSchemaData={data.strapiBlog?.FAQs?.FAQ}
        thumbnail={data.strapiBlog?.FeatureImage?.url}
        BlogData={data.strapiBlog}
        HowToSchema={data.strapiBlog.HowToSchema}
      />
      <BreadCrumb t={t} pageContext={pageContext} themeBlue>
        <CrumbLink
          href={`https://invideo.io/${
            language === 'en' ? '' : language + '/'
          }blog/`}
          text="Blog"
          property="item"
          themeBlue
          typeof="WebPage"
          title="Go to Academy for Professional Quality Video Creation."
          position={2}
        />
        <CrumbLink
          href={`https://invideo.io${pageContext.pageSlug}`}
          text={data.strapiBlog.Slug.split('-').join(' ')}
          isLast
          property="item"
          typeof="WebPage"
          title={data.strapiBlog.Title}
          position={3}
        />
      </BreadCrumb>
      <BlogHead>
        {data.strapiBlog.FeatureImage &&
          data.strapiBlog.ShowFeatureImageInHead !== false && (
            <picture id="poster-image">
              {data.strapiBlog.FeatureImage.ext.match(/gif/) ? (
                <img
                  src={data.strapiBlog.FeatureImage.url}
                  alt="Blog Poster"
                  sizes="(max-width: 800px) 100vw, 800px"
                  className="poster-image mx-auto feature-image w-full"
                />
              ) : (
                <React.Fragment>
                  <source
                    srcSet={data.strapiBlog.fields.posterImage.srcSetWebp}
                    sizes="(max-width: 800px) 100vw, 800px"
                    type="image/webp"
                  />
                  <source
                    srcSet={data.strapiBlog.fields.posterImage.srcSet}
                    type={`image/${
                      data.strapiBlog.fields.posterImage.ext || 'jpg'
                    }`}
                    sizes="(max-width: 800px) 100vw, 800px"
                  />
                  <img
                    sizes="(max-width: 800px) 100vw, 800px"
                    className="poster-image mx-auto feature-image w-full"
                    srcSet={data.strapiBlog.fields.posterImage.srcSet}
                    src={
                      data.strapiBlog.fields.posterImage.large.url ||
                      'https://d1nc6vzg2bevln.cloudfront.net/images/wp-assets/upload-video-to-instagram.jpeg'
                    }
                    alt="Poster"
                  />
                </React.Fragment>
              )}
            </picture>
          )}

        <h1 className="blog-title py-2 px-4 md:px-0">
          {data.strapiBlog.Title}
        </h1>

        <div className="author px-4 md:px-0 flex items-center">
          <div className="w-8 p-1 mr-2">
            <img
              className="rounded-full"
              src={
                data.strapiBlog?.author?.Avatar?.url?.replace(
                  '/origin/',
                  '/small/',
                ) ||
                'https://d2nb51khh83712.cloudfront.net/images/origin/3d_fish_b9782cfa14.png'
              }
              alt="Author"
            />
          </div>
          <div className="">
            <div className="author-name">
              <a
                href={`/blog/author/${
                  data.strapiBlog?.author?.Slug || 'admin'
                }/`}
              >
                {data.strapiBlog?.author?.Name || 'InVideo Team'}&nbsp;
              </a>
              <svg
                width="14"
                height="14"
                fill="currentColor"
                className="bi bi-clock-fill inline mx-2"
                viewBox="0 0 16 16"
              >
                <title>Clock Icon</title>
                <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z" />
              </svg>
              {data.strapiBlog.published_at !== data.strapiBlog.updated_at && (
                <span>Last Updated on: </span>
              )}
              <span>{data.strapiBlog.updated_at}</span>
            </div>
          </div>
        </div>
      </BlogHead>

      <BlogBody blogContent={data.strapiBlog.fields.BlogBody} />

      <BlogBottom
        path={props.path}
        slugPrefix={slugPrefix}
        prev={prev}
        next={next}
        latestBlogs={data.allStrapiBlog.edges}
        language={language}
        isLoggedIn={isLoggedIn}
      />
      <ScrollToTop />
      {state.leadMagnetModal.state && isModalVisible && (
        <LeadMagnetModal dispatch={dispatch} state={state.leadMagnetModal} />
      )}
    </Layout>
  );
}

export default BlogTemplate;

export const Pagequery = graphql`
  query ENBlogPage($id: String, $lang: String, $currentId: [String]) {
    strapiBlog(id: { eq: $id }) {
      Language
      Slug
      Title
      id
      author {
        Name
        username
        Slug
        Avatar {
          url
        }
        id
      }
      ShowFeatureImageInHead
      FeatureImage {
        url
        ext
      }
      fields {
        BlogBody
        posterImage {
          large {
            url
            webp
          }
          ext
          srcSet
          srcSetWebp
        }
      }
      published_at(formatString: "MMMM DD, YYYY")
      updated_at(formatString: "MMMM DD, YYYY")
      SEO {
        CanonicalURL
        Description
        Title
        id
        #Keywords
        RobotIndex
      }
      FAQs {
        FAQ {
          Answer
          Question
          id
        }
        id
      }
      HowToSchema {
        Title
        Description
        Image {
          url
        }
        id
        Step {
          StepTitle
          StepDescription
          StepImage {
            url
          }
          StepUrl
          id
        }
      }
      VideoSchema {
        Name
        Description
        UploadDate
        EmbedUrl
        Thumbnail {
          url
        }
      }
    }
    allStrapiBlog(
      sort: { fields: published_at, order: DESC }
      filter: { Language: { eq: $lang }, id: { nin: $currentId } }
      limit: 4
    ) {
      edges {
        node {
          id
          FeatureImage {
            name
            url
          }
          fields {
            posterImage {
              medium {
                url
                webp
              }
            }
          }
          author {
            Name
            username
            Slug
            Avatar {
              url
            }
            id
          }
          published_at(formatString: "MMMM DD, YYYY")
          Title
          Slug
        }
      }
    }
  }
`;

const BlogHead = styled.div`
  margin: 2rem auto;
  margin-top: 0;
  max-width: 812px;
  .feature-image {
    max-height: 580px;
    object-fit: cover;
    border-radius: 10px;
  }
  .blog-title {
    font-size: 36px;
    letter-spacing: 2px;
    font-weight: 700;
    line-height: 1.125;
    margin: 1rem auto;
    margin-top: 2.5rem;
  }
  .author {
    max-width: 812px;
    margin: 0 auto;
    font-size: 14px;
  }
`;
